import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MenuController, ModalController, Platform } from '@ionic/angular';
import { Campaign } from 'src/app/interfaces/campaign-interface';
import { PlatformResponse } from 'src/app/interfaces/webservices-interface';
import { AppLinksService } from 'src/app/services/applinks/app-links.service';
import { BannerService } from 'src/app/services/banner/banner.service';
import { isUndefined } from 'src/app/services/codeUtils';
import { ConfigService } from 'src/app/services/config/config.service';
import { EventsService } from 'src/app/services/event/event.service';
import { UserSessionService } from 'src/app/services/user-session/user-session.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { Navigator } from '../menu/navigator';

// import Swiper core and required modules
import SwiperCore, { Pagination, Navigation, Autoplay, SwiperOptions } from "swiper";
import { SwiperComponent } from 'swiper/angular';
import { Subscription } from 'rxjs';

SwiperCore.use([Autoplay, Pagination, Navigation]);
@Component({
  selector: 'app-banner-slider',
  templateUrl: './banner-slider.component.html',
  styleUrls: ['./banner-slider.component.scss'],
  encapsulation: ViewEncapsulation.None,
})

export class BannerSliderComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() lobbyName: any;
  @ViewChild('slides') slides: SwiperComponent;

  public swiperConfig: SwiperOptions = {
    autoplay: {
      delay: 10000,
      disableOnInteraction: false
    },
    pagination: {
      type: 'bullets',
      clickable: true
    },
    effect: 'slide',
    navigation: false,
    loop: true
  };

  public activeSlideDataLength: any;
  public autoPlayStopped: boolean = false;
  public dragged: boolean = false;
  public hideBannersList: boolean = false;
  public imagePath: string; //enable preview on localhost
  public slideData: any = "";
  public userLoggedIn: boolean = false;
  private subscr: Array<Subscription> = [];

  constructor(
    private appLinks: AppLinksService,
    private bannerService: BannerService,
    private config: ConfigService,
    private navigator: Navigator,
    private utils: UtilsService,
    private userSessionService: UserSessionService,
    protected navCtrl: ModalController,
    protected menuCtrl: MenuController,
    public events: EventsService,
    public platform: Platform,
    public userSession: UserSessionService,
  ) {

    this.imagePath = `${this.config?.getConfig()?.CDN_BASEURL}/assets/`;
  }

  ngOnInit() {
    this.initUserSession();
    this.subscribeToEvents();
    this.onGetBanners();
  }

  ionViewDidEnter() {
    this.onGetBanners();
  }

  ngAfterViewInit(): void {
    if (this.userLoggedIn) {
      this.slides.swiperRef.autoplay.start();
    }
  }

  ngOnDestroy(): void {
    this.subscr.forEach(s => s.unsubscribe());
  }

  private initUserSession(): void {

    this.userSessionService.isAuthenticated().then(res => {
      this.userLoggedIn = res;
    });

    this.subscr.push(this.userSessionService.getUserStatus().subscribe(userStatus => {
      this.userLoggedIn = !!userStatus;
    }));
  }

  private subscribeToEvents(): void {
    this.subscr.push(this.events.subscribe("event:login", () => { this.onGetBanners(); }));
    this.subscr.push(this.events.subscribe('event:authentication:logout', () => this.onGetBanners(true)));
    this.subscr.push(this.events.subscribe('event:update:banners', () => this.onGetBanners()));
    if (this.slides) {
      this.subscr.push(this.events.subscribe('event:slider:stop', () => this.slides?.swiperRef?.autoplay?.stop()));
      this.subscr.push(this.events.subscribe('event:slider:start', () => this.slides?.swiperRef?.autoplay?.start()));
    }
    this.subscr.push(this.events.subscribe('event:keywordaction', () => (this.hideBannersList = true)));
    this.subscr.push(this.events.subscribe('event:togglebanners', () => (this.hideBannersList = false)));
  }

  private onGetBanners(logoutState = false): void {
    // after a logout get the logout cache
    const lobbyName = logoutState ? '_logoutState' : this.lobbyName;

    this.bannerService.getBanners(lobbyName).then(
      (res: PlatformResponse<Array<Campaign>>) => {
        if (res?.result) {
          const banners = res?.payload?.filter(this.filteredBanners);
          const clonedBanners = this.utils.clone(banners);
          this.slideData = clonedBanners.map((resData) => {
            resData.banner_image_small = this.config.getCampaignBanerImgUrl(resData).sm;
            resData.banner_image_medium = this.config.getCampaignBanerImgUrl(resData).md;
            resData.banner_image_large = this.config.getCampaignBanerImgUrl(resData).lg;
            return resData;
          });

          if (this.slideData?.length > 1) this.swiperConfig.navigation = true;

          if (!isUndefined(this.slides) && this.slides) {
            // Stop autoplay
            this.slides?.swiperRef?.autoplay?.stop();
            this.autoPlayStopped = true;

            // Force a complete refresh of the Swiper instance
            setTimeout(() => {
              this.slides?.swiperRef?.update();
              this.slides?.swiperRef?.slideTo(0, 0);

              // Restart autoplay after a brief delay to ensure content is loaded
              setTimeout(() => {
                this.startAutoPlay();
              }, 100);
            });
          }

          this.activeSlideDataLength = this.slideData.length - 1;
        }
      }
    )
  }

  filteredBanners = (banner: Campaign) => {
    return !!~~banner.show_banner_flag;
  }

  onBannerButtonClick(item: Campaign) {
    switch (item.campaign_type) {
      case "MFREESPIN":
        this.navigator.navigateTo("MyFreeSpinComponent");
        break;
      case "COUPON_CODE":
        this.navigator.navigateTo("RedeemBonusCodeComponent", {}, { bonusCode: item.campaign_code });
        break;
      case "LEADERBOARD":
        this.navigator.navigateTo("RedeemBonusCodeComponent", {}, { bonusCode: item.campaign_code });
        break;
      case "DRAW":
        if (item.offer_type === 'LOYALTY_DRAW_ENTRY') {
          this.navigator.navigateTo("RedeemBonusCodeComponent", {}, { bonusCode: item.campaign_code });
        }
        break;
    }
  }

  onOpenExternalURL(item: Campaign): any {
    if (item.banner_url !== "" || item.banner_url != null) return this.appLinks.openExternalPage(item.banner_url);
  }

  onClickLogoutState(item: Campaign) {
    if (item.banner_action == "SIGNUP") {
      this.navigator.navigateTo("SignupStepOneComponent", {}, { signupcode: item.campaign_code });
    } else {
      this.navigator.navigateTo("LoginComponent", { forceModal: true });
    }
  }

  isDeposit(item) {
    return item.campaign_type === "DEPOSIT" || item.campaign_type === "REFER_FRIEND" ? true : false;
  }

  /**
   * removed ionic 3 loop from view and replaced with the code below since loop has a bug which breaks click event attached to a button inside the slider when user goes from last slide to first
   * code below could be removed once ionic fixes the slider issue
   * https://forum.ionicframework.com/t/slides-click-event-not-working-in-the-last-slide/65859/8
   */
  startAutoPlay() {
    if (this.autoPlayStopped === true && !this.dragged) {
      this.slides.swiperRef.autoplay.start();
      this.autoPlayStopped = false;
    }
  }

  stoppedAutoplay() {
    this.autoPlayStopped = true;
  }

  slideDragged() {
    this.dragged = true;
  }

  next() {
    if (this.slides.swiperRef.isEnd) {
      this.slides.swiperRef.slideTo(0, 600);
    } else {
      this.slides.swiperRef.slideNext(600);
    }
  }

  prev() {
    if (this.slides.swiperRef.isBeginning) {
      this.slides.swiperRef.slideTo((this.activeSlideDataLength), 600);
    } else {
      this.slides.swiperRef.slidePrev(600);
    }
  }

}
